import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Rocket, SlidersHorizontal, Check, Circle } from 'lucide-react';
import { Button } from '@/shared/ui/Button';
import { useLocation } from '@/shared/hooks/useLocation';
import { useToast } from '@/shared/ui/Toast';
import { campaignsApi } from '../api/campaignsApi';
import { campaignKeys } from '../queryKeys';
import styles from './ModeSelectionScreen.module.css';

interface Props {
  onChooseAutopilot: () => void;
  onChooseGuided: () => void;
  onCancel: () => void;
}

/**
 * Mode-selection screen shown once per location: when the user has approved a
 * strategy but `campaignApprovalPreference` is still NULL. The selected mode
 * is persisted before the wizard opens, so future recommendation rounds skip
 * this screen entirely.
 */
export function ModeSelectionScreen({ onChooseAutopilot, onChooseGuided, onCancel }: Props) {
  const { currentLocation } = useLocation();
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  const saveMode = useMutation({
    mutationFn: (mode: 'autopilot' | 'guided') =>
      campaignsApi.autopilot.updateConfig({
        locationId: currentLocation!.id,
        campaignApprovalPreference: mode,
      }),
    onSuccess: (_data, mode) => {
      queryClient.invalidateQueries({
        queryKey: campaignKeys.locationAdsConfig(currentLocation?.id ?? ''),
      });
      (mode === 'autopilot' ? onChooseAutopilot : onChooseGuided)();
    },
    onError: (err: unknown) => {
      showToast((err as Error).message ?? 'Could not save mode', 'error');
    },
  });

  const choose = (mode: 'autopilot' | 'guided') => {
    if (!currentLocation?.id || saveMode.isPending) return;
    saveMode.mutate(mode);
  };

  return (
    <div className={styles.wrap}>
      <div className={styles.heading}>
        <h1 className={styles.title}>How do you want to run your ads going forward?</h1>
        <p className={styles.subtitle}>Choose your level of involvement. You can switch anytime.</p>
      </div>

      <div className={styles.grid}>
        <div
          className={`${styles.card} ${styles.cardRecommended}`}
          onClick={() => choose('autopilot')}
        >
          <div className={styles.recPill}>Recommended</div>
          <div className={styles.cardIcon}>
            <Rocket size={28} strokeWidth={1.75} aria-hidden />
          </div>
          <div className={styles.cardTitle}>We handle everything</div>
          <p className={styles.cardSubtitle}>
            Tell us your budget. We’ll set up, optimize, and report.
          </p>
          <ul className={styles.cardBullets}>
            {[
              'You set budget and goals',
              'We choose channels & creative',
              'You approve the plan once',
              'We handle the rest',
            ].map((b) => (
              <li key={b}>
                <span className={styles.bulletCheck}>
                  <Check size={14} aria-hidden />
                </span>{' '}
                {b}
              </li>
            ))}
          </ul>
          <Button
            onClick={() => choose('autopilot')}
            isLoading={saveMode.isPending && saveMode.variables === 'autopilot'}
          >
            Choose this →
          </Button>
        </div>

        <div className={styles.card} onClick={() => choose('guided')}>
          <div className={styles.cardIcon}>
            <SlidersHorizontal size={28} strokeWidth={1.75} aria-hidden />
          </div>
          <div className={styles.cardTitle}>I want more control</div>
          <p className={styles.cardSubtitle}>
            Review each campaign, approve creative, manage targeting.
          </p>
          <ul className={styles.cardBullets}>
            {[
              'Review each campaign individually',
              'Set budgets per campaign',
              'Approve image creative (24h SLA)',
              'Final approve at the end',
            ].map((b) => (
              <li key={b}>
                <span className={styles.bulletCheck} data-variant="alt">
                  <Circle size={12} strokeWidth={2} aria-hidden />
                </span>{' '}
                {b}
              </li>
            ))}
          </ul>
          <Button
            variant="secondary"
            onClick={() => choose('guided')}
            isLoading={saveMode.isPending && saveMode.variables === 'guided'}
          >
            Choose this →
          </Button>
        </div>
      </div>

      <div className={styles.deferRow}>
        <Button variant="tertiary" size="sm" onClick={onCancel}>
          I'll decide later
        </Button>
      </div>
    </div>
  );
}
